import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as R from 'ramda'
import { redirect } from 'redux-first-router'
import { toTemplates } from 'ducks/router'
import { useDeleteJobModal } from 'components/modals/DeleteJobModal/hooks'
import { mapInternalStateToPayload as mapJobPositionStateToPayload } from 'components/modals/common/JobPositionModal/actions'
import { getLanguage, getUser, getFeatureFlags } from 'ducks/auth'
import { useModal, useSnackbar } from 'hooks'
import { ACTIONS, getActions } from './model'
import { DEFAULT_JOBS_QUERY_PARAMS } from 'constants/projects'
import { jobTemplates, jobs } from 'orm'
import { postInterviewTemplate } from 'api/interviewTemplates'
import { toProjects } from 'ducks/router'
import { getDefaultTemplate } from 'components/pages/Jobs/JobListing/hooks'
import { t } from 'ttag'

const useJobDetailsActions = handlers => {
  const { handleDeleteJobModalOpen } = handlers

  const actions = React.useMemo(
    () =>
      R.map(action => {
        const mapTypeToOnClick = {
          [ACTIONS.DELETE]: handleDeleteJobModalOpen,
        }

        return {
          ...action,
          onClick: mapTypeToOnClick[action.type],
        }
      }, getActions()),
    [handleDeleteJobModalOpen],
  )

  return {
    state: { actions },
  }
}

const useCreateTemplateModal = () => {
  const dispatch = useDispatch()

  const { isOpen, handleClose, handleOpen } = useModal()

  const handleConfirm = React.useCallback(
    (data, { onFailureAction } = {}) => {
      dispatch(
        jobTemplates.postStart({
          data,
          onFailureAction,
          postSuccessAction: ({ id }) => {
            handleClose()
            dispatch(toTemplates('jobs', id))
          },
        }),
      )
    },
    [dispatch, handleClose],
  )

  return {
    handleCreateTemplateModalClose: handleClose,
    handleCreateTemplateModalConfirm: handleConfirm,
    handleCreateTemplateModalOpen: handleOpen,
    isCreateTemplateModalOpen: isOpen,
  }
}

const useEditPositionModal = state => {
  const { id } = state

  const dispatch = useDispatch()
  const user = useSelector(getUser)
  const featureFlags = useSelector(getFeatureFlags)

  const { isOpen, handleClose, handleOpen } = useModal()

  const {
    handlers: { handleShowSnackbar },
  } = useSnackbar()

  const handleConfirm = React.useCallback(
    async (data, isDefaultTamplate, { onError, onSuccess }) => {
      const payload = mapJobPositionStateToPayload(data)
      const templateValue = data?.template?.value

      const isCurrentUserAccessRevoked =
        !featureFlags.accountAdmin &&
        !payload.members_with_access.includes(user.pk)

      if (templateValue && isDefaultTamplate) {
        const { error, meta, response } = await postInterviewTemplate(
          getDefaultTemplate(data, templateValue),
        )

        if (!response && onError) {
          onError({ message: error, type: meta.HTTPstatus })
          return
        }
      }

      dispatch(
        jobs.patchStart({
          pk: id,
          data: payload,
          onFailureAction: onError,
          onSuccessAction: !isCurrentUserAccessRevoked
            ? jobs.fetchStart({ pk: id })
            : undefined,
          postSuccessAction: () => {
            handleClose()
            if (onSuccess) {
              onSuccess()
            }

            if (isCurrentUserAccessRevoked) {
              dispatch(
                redirect(toProjects(null, null, DEFAULT_JOBS_QUERY_PARAMS)),
              )

              handleShowSnackbar({
                message: t`You have revoked your permission to access ${payload.name}`,
                type: 'info',
                timeout: 10000,
              })
            }
          },
        }),
      )
    },
    [id, dispatch, handleClose, user, featureFlags],
  )

  return {
    handleEditPositionModalClose: handleClose,
    handleEditPositionModalConfirm: handleConfirm,
    handleEditPositionModalOpen: handleOpen,
    isEditPositionModalOpen: isOpen,
  }
}

export const useJobDetailsTab = state => {
  const { id } = state

  const language = useSelector(getLanguage)

  const {
    handleEditPositionModalClose,
    handleEditPositionModalConfirm,
    handleEditPositionModalOpen,
    isEditPositionModalOpen,
  } = useEditPositionModal({ id })

  const {
    handleDeleteJobModalClose,
    handleDeleteJobModalConfirm,
    handleDeleteJobModalOpen,
    isDeleteJobModalOpen,
  } = useDeleteJobModal({ id })

  const {
    handleCreateTemplateModalClose,
    handleCreateTemplateModalConfirm,
    handleCreateTemplateModalOpen,
    isCreateTemplateModalOpen,
  } = useCreateTemplateModal()

  const {
    state: { actions },
  } = useJobDetailsActions({ handleDeleteJobModalOpen })

  jobTemplates.useModel()

  return {
    state: {
      actions,
      language,
      isDeleteJobModalOpen,
      isEditPositionModalOpen,
      isCreateTemplateModalOpen,
    },
    handlers: {
      handleDeleteJobModalClose,
      handleDeleteJobModalConfirm,
      handleEditPositionModalClose,
      handleEditPositionModalConfirm,
      handleEditPositionModalOpen,
      handleCreateTemplateModalClose,
      handleCreateTemplateModalConfirm,
      handleCreateTemplateModalOpen,
    },
  }
}
